/**
 * @fileoverview Component for business selector modal
 *
 * @author Varun Vijayaraghavan (vvijayaraghavan@yext.com)
 */
goog.module('yext.m4.confcode.adminconsole.BusinessSelectorModal');
goog.require('yext');

const {SmallDialog} = goog.require('yext.ui.components.modal.Dialog');
const {Checkbox} = goog.require('yext.ui.components.Checkbox');
const {Radio} = goog.require('yext.ui.components.Radio');
const {Input} = goog.require('yext.ui.components.input');

/**
 * Child component that renders the auth URL.
 *
 * @param {!AuthFlowProps} props
 * @returns {null|*}
 * @constructor
 */
function AuthFlow(props) {
  const url = props.url;
  return (
    <div className="auth-section">
      <p>Click link below to receive access to configure this account and hit "Continue" once the
        authorization flow is complete</p>
      <p><a className="sl-start-auth-flow" target="_blank" rel="noreferrer" href={url}>Start authorization flow</a></p>
      <p>Contact your admin or <a href=" https://help.yext.com/hc/en-us/requests/new" target="_blank">https://help.yext.com/hc/en-us/requests/new</a> for assistance.</p>
    </div>
  );
}

/**
 * Child component that renders the radio buttons and input for selecting the business
 *
 * @param {!BusinessSelectorProps} props
 * @returns {React.ReactNode}
 * @constructor
 */
function BusinessSelector(props) {
  return (
    <div className={'business-selector'}>
      <Radio
        id="current-business-id"
        label={`${props.businessSelectorFor === Props.Mode.DIFF ? 'View diffs for': 'Apply to'} this account (${props.currentBusinessId})`}
        value={props.useCurrentBusinessId}
        checked={props.useCurrentBusinessId}
        onChange={() => {
          props.radioButtonHandler(true);
        }}
        name="current-business-id"
        tid="sl-current-business-radio-button">
      </Radio>
      <Radio
        id="select-business-id"
        label={`${props.businessSelectorFor === Props.Mode.DIFF ? 'View diffs for': 'Apply to'} another account`}
        value={props.useCurrentBusinessId}
        checked={!props.useCurrentBusinessId}
        onChange={() => {
          props.radioButtonHandler(false);
        }}
        name="select-business-id"
        tid="sl-different-business-radio-button">
      </Radio>
      <Input /* eslint-disable-line yext/a11y-empty-input-label */ label=""
        value={props.inputBusinessId}
        onChange={props.businessIdInputHandler}
        required={true}
        valid={props.validBusinessId}
        disabled={props.useCurrentBusinessId}
        id="business-id-input"
        hideLabel={true}
        placeholder="Business ID"
        tid="sl-business-id-input"/>
    </div>);
}

/**
 * Child component that renders radio buttons for handling dependencies
 *
 * @param {!DependencyFileProps} props
 * @returns {*}
 * @constructor
 */
function MissingDependencyBehavior(props) {
  return (
    <div id="missing-dependencies">
      <div className="description">
        <h6>{'Missing Dependency Behavior'}</h6>
        <p>{'What should happen if there\'s one or more missing dependencies?'}</p>
      </div>
      <Radio
        id="error"
        label="Display Error"
        value="ERROR"
        checked={props.missingDependencyBehavior === 'ERROR'}
        onChange={() => {
          props.onChange('ERROR');
        }}
        helpText="Do not apply resources, instead display an error message."
        name="error"/>
      {
        props.allowAddingDependencies
          && <Radio
            id="add"
            label="Add Missing Dependencies"
            value="add"
            checked={props.missingDependencyBehavior === 'ADD'}
            onChange={() => {
              props.onChange('ADD');
            }}
            helpText="Add any missing dependencies to this account, and continue applying."
            name="add"/>
      }
      <Radio
        id="ignore"
        label="Ignore Missing Dependencies"
        value="ignore"
        checked={props.missingDependencyBehavior === 'IGNORE'}
        onChange={() => {
          props.onChange('IGNORE');
        }}
        helpText="Ignore any missing dependencies, and continue applying."
        name="ignore"/>
    </div>
  );
}

/**
 * @param {!BusinessIdInputProps} props
 * @constructor
 */
function BusinessIdInput(props) {
  return (
    <div className="business-selector">
      <Input
        value={props.inputBusinessId}
        onChange={props.businessIdInputHandler}
        required={true}
        valid={props.validBusinessId}
        id="business-id-input"
        label="Enter Account ID"
      />
    </div>
  );
}

/**
 * @param {!NamespaceInputProps} props
 * @constructor
 */
function NamespaceInput(props) {
  return (
    <div className="namespace-selector">
      <Input
        value={props.inputNamespace}
        onChange={props.namespaceInputHandler}
        required={false}
        id="namespace-input"
        label="Namespaces (optional):"
      />
    </div>
  );
}

class BusinessSelectorModal extends React.Component {
  constructor(props) {
    super(props);

    /**
     * @type {!State}
     */
    this.state = {
      showDialog: true,
      useCurrentBusinessId: !!this.props.currentBusinessId, // only set when businessId is present.
      businessIdValid: true,
      inputBusinessId: null,
      authUrl: null,
      missingDependencyBehavior: 'ERROR',
      accessError: false,
      errorMessage: null,
      sessionId: undefined,
      hasBusinessIdInputChangedSinceLastAuthAttempt: false,
      inputNamespace: this.props.sourceNamespace,
      isDry: false,
      isComprehensive: false,
    };

    /**
     * @type {!Props}
     */
    this.props;

    this._hideDialog = this._hideDialog.bind(this);
    this._clickContinue = this._clickContinue.bind(this);
  }

  /**
   * Function that's called when the dialog is closed.
   * @private
   */
  _hideDialog() {
    this.setState({showDialog: false});
  }

  componentDidUpdate(prevProps) {
    // when prop refs are changed, it implies that the modal has been invoked again with a new set
    // of props. always open, in this case.
    // Additionally, reset some (not all) of the other state, conditionally.
    if (prevProps != this.props) {
      this.setState(state => ({
        showDialog: true,
        accessError: false,
        authUrl: null,
        businessIdValid: true,
        inputBusinessId: state.businessIdValid ? state.inputBusinessId : null,
        errorMessage: null,
        sessionId: undefined,
      }));
    }
  }

  /**
   * Attempts to Complete the session for a given business. If the session is successfully
   * completed, then a call to checkBusinessAccess is made. Otherwise, do nothing (i.e.
   * continue displaying the start authorization flow URL).
   *
   * @private
   * @return {!Promise}
   */
  _completeSession(selectedBusinessId) {
    return this.props.completeSession(selectedBusinessId, this.state.sessionId)
      .then(response => {
        const isSessionComplete = response['isSessionComplete'];
        if (isSessionComplete) {
          return this._checkBusinessAccess(selectedBusinessId);
        }
      });
  }

  /**
   * Checks for access to the business ID. If the user has access to the business, call the
   * onSelect callback and close this modal. Otherwise, store the session ID and display the
   * start authorization flow URL.
   *
   * @private
   * @return {!Promise}
   */
  _checkBusinessAccess(selectedBusinessId) {
    return this.props.checkBusinessAccess(selectedBusinessId, this.state.sessionId)
      .then(
        response => {
          const hasAccess = response['hasAccess'];
          const businessName = response['businessName'];
          const authUrl = response['authUrl'];
          const sessionId = response['sessionId'];

          if (hasAccess) {
            // User has access to selected business, close modal
            this.props.onSelect(
              /** @type {!number} */(selectedBusinessId),
              businessName,
              this.state.missingDependencyBehavior,
              this.state.inputNamespace,
              this.state.isComprehensive,
              this.state.isDry);
            this.setState({
              authUrl: null,
              accessError: false,
              sessionId: undefined,
              hasBusinessIdInputChangedSinceLastAuthAttempt: false,
            });
            this._hideDialog();
          } else {
            // User doesn't have access to selected business, show auth URL
            // to tell them to enter auth flow
            this.setState({
              authUrl: authUrl,
              accessError: false,
              sessionId: sessionId,
              hasBusinessIdInputChangedSinceLastAuthAttempt: false,
            });
          }
        },
        () => {
          const errorMessage = 'An error occurred. Contact your admin or support if this issue persists.';
          this.setState({
            accessError: true,
            errorMessage: errorMessage,
          });
        });
  }

  /**
   * Event handler when the "Continue" button is present. If a session ID is present and the
   * the business ID input has not changed since the last auth attempt (i.e. since the last
   * time "Continue" was clicked, call completeSession. Otherwise, call checkHasBusinessAccess.
   *
   * @private
   */
  _clickContinue() {
    const selectedBusinessId = this.state.useCurrentBusinessId
      ? this.props.currentBusinessId
      : this.state.inputBusinessId;

    if (this.state.sessionId && !this.state.hasBusinessIdInputChangedSinceLastAuthAttempt) {
      return this._completeSession(selectedBusinessId);
    } else {
      return this._checkBusinessAccess(selectedBusinessId);
    }
  }

  /**
   * @suppress {checkTypes}
   */
  render() {
    const callToAction = {
      primary: {
        type: 'active',
        text: yext.msg('Continue'),
        callback: this._clickContinue,
        tid: 'sl-continue-button',
        disabled:
          (!this.state.useCurrentBusinessId
            && (!this.state.businessIdValid || !this.state.inputBusinessId)),
      },
      cancel: {
        type: 'cancel',
        tid: 'sl-cancel-button',
        text: yext.msg('Cancel'),
      },
    };

    const dialogTestId = this.props.businessSelectorFor === Props.Mode.DIFF
      ? 'sl-diff-dialog'
      : 'sl-apply-dialog';

    return (
      <SmallDialog
        title={
          yext.msg('Select Account To {0}', this.props.businessSelectorFor === Props.Mode.DIFF ? 'View Diffs For' : 'Apply To')
        }
        show={this.state.showDialog}
        onHideDialog={this._hideDialog}
        callToAction={callToAction}
        tid={dialogTestId}>

        {/* Render radio boxes if business Id is present*/}
        {!!this.props.currentBusinessId
          && <BusinessSelector
            currentBusinessId={this.props.currentBusinessId}
            useCurrentBusinessId={this.state.useCurrentBusinessId}
            inputBusinessId={this.state.inputBusinessId}
            validBusinessId={this.state.businessIdValid}
            businessIdInputHandler={
              businessId =>
                this.setState({
                  inputBusinessId: businessId,
                  businessIdValid: !isNaN(businessId),
                  hasBusinessIdInputChangedSinceLastAuthAttempt: true,
                  authUrl: null,
                })
            }
            radioButtonHandler={
              useCurrentBusinessId => this.setState({
                useCurrentBusinessId,
                authUrl: null,
              })
            }
            businessSelectorFor={this.props.businessSelectorFor}
          />
        }

        {/* Render simple input if businessId is not present*/}
        {!this.props.currentBusinessId
          && <BusinessIdInput
            inputBusinessId={this.state.inputBusinessId}
            validBusinessId={this.state.businessIdValid}
            businessIdInputHandler={
              businessId =>
                this.setState({
                  inputBusinessId: businessId,
                  businessIdValid: !isNaN(businessId),
                  hasBusinessIdInputChangedSinceLastAuthAttempt: true,
                  authUrl: null,
                })
            }
          />
        }

        {/* Render apply/diff mode radio if the enableComprehensiveApply param is present */}
        {this.props.shouldEnableComprehensiveApply
          && <Checkbox
            id="isComprehensive"
            label="Comprehensive"
            checked={this.state.isComprehensive}
            helpText="Persist all changes. Resources that are not present will be removed."
            onChange={() =>
              this.setState(state => ({isComprehensive: !state.isComprehensive}))
            }
            disabled={!this.props.shouldEnableComprehensiveApply}
          ></Checkbox>
        }

        {/* Render dry apply checkbox if the mode is apply*/}
        {this.props.businessSelectorFor === Props.Mode.APPLY
          && <Checkbox
            id="isDry"
            label="Dry"
            checked={this.state.isDry}
            helpText="Validate and view the result of applying resources without persisting changes"
            onChange={() =>
              this.setState(state => ({isDry: !state.isDry}))
            }
          ></Checkbox>
        }

        {/* Render namespaces input*/}
        {
          <NamespaceInput
            inputNamespace={this.state.inputNamespace}
            namespaceInputHandler={
              namespace =>
                this.setState({
                  inputNamespace: namespace === '' ? null : namespace,
                })
            }
          />
        }

        {/* Render error if present*/}
        {
          !!this.state.accessError
          && <div className="modal-error">
            <p>{this.state.errorMessage}</p>
          </div>
        }

        {/* Render missing dependencies checkbox. .missing-dependencies className added for styling*/}
        {this.props.shouldHandleMissingDependencies
          && <MissingDependencyBehavior
            missingDependencyBehavior={this.state.missingDependencyBehavior}
            allowAddingDependencies={this.props.allowAddingDependencies}
            onChange={missingDependencyBehavior => this.setState({missingDependencyBehavior})}
          />
        }

        {/* Render auth link and description, if not yet authorized*/}
        {!!this.state.authUrl && <AuthFlow url={this.state.authUrl}/>}
      </SmallDialog>
    );
  }
}

/**
 * @record
 */
class State {
  constructor() {
    /**
     * @type {!boolean}
     */
    this.showDialog;

    /**
     * @type {?boolean}
     */
    this.useCurrentBusinessId;


    /**
     * @type {number|null}
     */
    this.inputBusinessId;


    /**
     * @type {string|null}
     */
    this.authUrl;

    /**
     * @type {!boolean}
     */
    this.businessIdValid;

    /**
     * @type {!string}
     */
    this.missingDependencyBehavior;

    /**
     * @type {!boolean}
     */
    this.accessError;

    /**
     * @type {string|null}
     */
    this.errorMessage;

    /**
     * Stores the current session ID. It is necessary to keep track of the latest session ID
     * because the "checkBusinessAccess" endpoint stores the access token content in a
     * browser cookie and the access token content is retrieved from the session ID.
     *
     * @type {string|undefined}
     */
    this.sessionId;

    /**
     * @type {!boolean}
     */
    this.hasBusinessIdInputChangedSinceLastAuthAttempt;

    /**
     * @type {string|null}
     */
    this.inputNamespace;

    /**
     * @type {!boolean}
     */
    this.isDry;

    /**
     * @type {!boolean}
     */
    this.isComprehensive;
  }
}

/**
 * @record
 */
class Props {
  constructor() {
    /**
     * @type {!function(!number, !string, !string, (null|string), !boolean, !boolean)}
     */
    this.onSelect;

    /**
     * @type {?number}
     */
    this.currentBusinessId;

    /**
     * @type {!boolean}
     */
    this.shouldHandleMissingDependencies;

    /**
     * @type {!function(!number, (undefined|string)): Promise}
     */
    this.checkBusinessAccess;

    /**
     * @type {!function(!number, (undefined|string)): Promise}
     */
    this.completeSession;

    /**
     * @type {!Props.Mode}
     */
    this.businessSelectorFor;

    /**
     * @type {!boolean}
     */
    this.allowAddingDependencies;

    /**
     * @type {?string}
     */
    this.sourceNamespace;


    /**
     * @type {!boolean}
     */
    this.shouldEnableComprehensiveApply;
  }
}

/**
 * @enum {!string}
 */
Props.Mode = {
  DIFF: 'diff',
  APPLY: 'apply',
};


/**
 * @record
 */
class BusinessSelectorProps {
  constructor() {
    /**
     * @type {!boolean}
     */
    this.useCurrentBusinessId;
    /**
     * @type {!number}
     */
    this.currentBusinessId;
    /**
     * @type {!number}
     */
    this.inputBusinessId;
    /**
     * @type {!boolean}
     */
    this.validBusinessId;
    /**
     * @type {!function(!number)}
     */
    this.businessIdInputHandler;
    /**
     * @type {!function(!boolean)}
     */
    this.radioButtonHandler;
    /**
     * @type {!Props.Mode}
     */
    this.businessSelectorFor;

    /**
     * @type {?string}
     */
    this.sourceNamespace;
  }
}

/** @record */
class BusinessIdInputProps {
  constructor() {
    /** @type {!number} */
    this.inputBusinessId;
    /** @type {!boolean} */
    this.validBusinessId;
    /** @type {!function(!number)} */
    this.businessIdInputHandler;
  }
}

/** @record */
class NamespaceInputProps {
  constructor() {
    /** @type {string} */
    this.inputNamespace;
    /** @type {!function(string)} */
    this.namespaceInputHandler;
  }
}

/** @record */
class DependencyFileProps {
  constructor() {
    /** @type {!string} */
    this.missingDependencyBehavior; // 'IGNORE', 'ERROR', 'ADD'
    /** @type {!boolean} */
    this.allowAddingDependencies;
    /** @type {!function(!string)} */
    this.onChange;
  }
}

/**
 * @record
 */
class AuthFlowProps {
  constructor() {
    /**
     * @type {!string}
     */
    this.url;
  }
}


// Some of these exports are for tests
exports.BusinessSelectorModal = BusinessSelectorModal;
exports.BusinessSelector = BusinessSelector;
exports.AuthFlow = AuthFlow;
exports.MissingDependencyBehavior = MissingDependencyBehavior;
